import { Controller } from "@hotwired/stimulus"

// Multi-chip label filter input. Values submit as labels[].
export default class extends Controller {
  static targets = ["list", "input", "template"]

  connect() {
    this.form = this.element.closest("form")
    this.boundPrepareSubmit = this.prepareSubmit.bind(this)
    this.boundRefocusAfterSearch = this.refocusAfterSearch.bind(this)
    this.form?.addEventListener("submit", this.boundPrepareSubmit)
    this.form?.addEventListener("turbo:submit-end", this.boundRefocusAfterSearch)
    this.editing = false
    this.refocusAfterSubmit = false
    this.defaultPlaceholder = this.inputTarget.getAttribute("placeholder") || ""
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.boundPrepareSubmit)
    this.form?.removeEventListener("turbo:submit-end", this.boundRefocusAfterSearch)
  }

  refocusAfterSearch(event) {
    if (event.detail?.success === false) return
    if (!this.refocusAfterSubmit) return

    this.refocusAfterSubmit = false

    requestAnimationFrame(() => {
      this.inputTarget.focus({ preventScroll: true })
    })
  }

  focusInput(event) {
    if (event.target.closest("[data-filter-labels-chip]")) return
    if (event.target === this.inputTarget) return

    this.inputTarget.focus()
  }

  focusOut(event) {
    if (this.element.contains(event.relatedTarget)) return

    requestAnimationFrame(() => {
      if (document.activeElement === this.inputTarget) return

      const pending = this.inputTarget.value.trim()
      if (pending) {
        this.addFromInput()
        this.submitUnlessSearchClick(event.relatedTarget)
        return
      }

      this.restoreInputPosition()
      if (this.editing) {
        this.editing = false
        this.submitUnlessSearchClick(event.relatedTarget)
      }
    })
  }

  submitUnlessSearchClick(next) {
    if (next?.closest?.('button[type="submit"], input[type="submit"]')) return

    this.submitForm()
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()

      if (this.inputTarget.value.trim() === "") {
        this.restoreInputPosition()
        this.editing = false
        this.submitForm()
        return
      }

      this.addFromInput()
      this.submitForm()
      return
    }

    if (event.key === "Backspace" && this.inputTarget.value === "") {
      this.removeLastChip()
    }
  }

  remove(event) {
    event.stopPropagation()
    event.currentTarget.closest("[data-filter-labels-chip]")?.remove()
    this.submitForm()
  }

  editKeydown(event) {
    if (event.target.closest("button")) return
    if (event.key !== "Enter" && event.key !== " ") return

    event.preventDefault()
    this.edit(event)
  }

  edit(event) {
    if (event.target.closest("button")) return

    if (this.inputTarget.value.trim()) {
      this.addFromInput()
    } else {
      this.restoreInputPosition()
    }

    const chip = event.currentTarget
    const value = chip.querySelector('input[type="hidden"]')?.value || ""
    const next = chip.nextElementSibling

    chip.remove()

    if (next) {
      this.listTarget.insertBefore(this.inputTarget, next)
    } else {
      this.listTarget.appendChild(this.inputTarget)
    }

    this.editing = true
    this.inputTarget.classList.add("filter-labels__input--editing")
    this.inputTarget.placeholder = ""
    this.inputTarget.value = value
    this.resizeInput()
    this.inputTarget.focus({ preventScroll: true })
    const caret = value.length
    this.inputTarget.setSelectionRange(caret, caret)
  }

  resizeInput() {
    if (!this.inputTarget.classList.contains("filter-labels__input--editing")) {
      this.inputTarget.style.width = ""
      return
    }

    const value = this.inputTarget.value
    this.inputTarget.style.width = `${Math.max(value.length + 1, 4)}ch`
  }

  prepareSubmit() {
    this.addFromInput()
  }

  submitForm() {
    this.refocusAfterSubmit = true
    this.form?.requestSubmit()
  }

  addFromInput() {
    const value = this.inputTarget.value.trim()
    if (!value) {
      this.restoreInputPosition()
      this.editing = false
      return
    }

    const before =
      this.inputTarget.parentElement === this.listTarget ? this.inputTarget : null

    if (!this.currentValues.includes(value)) {
      this.appendChip(value, before)
    }

    this.inputTarget.value = ""
    this.restoreInputPosition()
    this.editing = false
  }

  appendChip(value, before = null) {
    const node = this.templateTarget.content.cloneNode(true)
    const chip = node.querySelector("[data-filter-labels-chip]")
    const input = chip.querySelector('input[type="hidden"]')
    const text = chip.querySelector("[data-filter-labels-text]")

    input.value = value
    text.textContent = value
    chip.title = value

    if (before) {
      this.listTarget.insertBefore(chip, before)
    } else {
      this.listTarget.appendChild(chip)
    }
  }

  restoreInputPosition() {
    this.inputTarget.classList.remove("filter-labels__input--editing")
    this.inputTarget.style.width = ""
    this.inputTarget.placeholder = this.defaultPlaceholder
    this.listTarget.after(this.inputTarget)
  }

  removeLastChip() {
    const chips = this.chipElements
    if (!chips.length) return

    chips[chips.length - 1].remove()
    this.submitForm()
  }

  get chipElements() {
    return this.listTarget.querySelectorAll("[data-filter-labels-chip]")
  }

  get currentValues() {
    return Array.from(this.chipElements).map((chip) => {
      return chip.querySelector('input[type="hidden"]')?.value
    })
  }
}
